import { yupResolver } from '@hookform/resolvers/yup';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { FullPageLoading } from '../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { PageWrapper } from '../../../CommonComponents/PageWrapper/PageWrapper';
import { showOPLConfirmationDialog } from '../../../helpers/showOPLConfirmationDialog';
import { FirmSettingsDTO, useFirmSettingsQuery } from './api/useFirmSettingsQuery';
import { useSaveFirmSettingsMutation } from './api/useSaveFirmSettingsMutation';
import { AssetBucketing } from './components/AssetBucketing/AssetBucketing';
import { Assumptions } from './components/Assumptions/Assumptions';
import { AutoAccountsAgreements } from './components/AutoAccountsAgreements/AutoAccountsAgreements';
import { CustomDisclosure } from './components/CustomDisclosure/CustomDisclosure';
import { FirmLogo } from './components/FirmLogo/FirmLogo';
import { BeFi20 } from './components/BeFi20/BeFi20';
import { LandingPage } from './components/LandingPage/LandingPage';
import { ManualAccounts } from './components/ManualAccounts/ManualAccounts';
import { SharingSettings } from './components/SharingSettings/SharingSettings';
import { SupportEmails } from './components/SupportEmails/SupportEmails';
import { TermsAndConditions } from './components/TermsAndConditions/TermsAndConditions';
import {
  FormValues,
  parseDefaultValues,
  parseSubmittedValues,
  validation,
} from './FirmSettings.meta';
import { getApplicationVariables } from '../../../AdviserArea/AdviserAreaActions';
import { PulseCheck } from './components/PulseCheck/PulseCheck';
import { MobileAppNotifications } from './components/MobileAppNotifications/MobileAppNotifications';
import { PersonalFinances } from './components/PersonalFinances/PersonalFinances';
import { CurrentYearAssumptionSetting } from '../../../CommonComponents/CurrentYearAssumptionSetting/CurrentYearAssumptionSetting';
import { ManageExcessCashModal } from '../../../ManageExcessCashModal/ManageExcessCashModal';
import { AIPdfReader } from './components/AIPdfReader/AIPdfReader';
import { DocumentVault } from './components/DocumentVault/DocumentVault';

export const FirmSettings = () => {
  const { isLoading, isError, refetch, data } = useFirmSettingsQuery();

  if (isLoading) {
    return <FullPageLoading />;
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" my="56">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <PageWrapper>
      <FirmSettingsForm firmSettings={data} />
    </PageWrapper>
  );
};

interface Props {
  firmSettings: FirmSettingsDTO;
}

const FirmSettingsForm: React.FC<Props> = ({ firmSettings }) => {
  const [showCurrentYearAssumptionSetting, setCurrentYearAssumptionSettingVisibility] = useState(
    false,
  );
  const [isManageExcessCashModalOpen, setIsManageExcessCashModalOpen] = useState(false);

  const dispatch = useDispatch();

  const formBag = useForm<FormValues>({
    defaultValues: parseDefaultValues(firmSettings),
    resolver: yupResolver(validation),
  });

  const {
    mutateAsync: saveFirmSettings,
    isLoading: isSavingFirmSettings,
    error: saveFirmSettingsError,
  } = useSaveFirmSettingsMutation();

  useEffect(() => {
    if (isEmpty(formBag.formState.dirtyFields)) {
      ADV.util.Router.unregisterNavigateAwayCallback();
    } else {
      ADV.util.Router.registerNavigateAwayCallback(showOPLConfirmationDialog);
    }
  }, [formBag.formState]);

  const onSubmit = async (values: FormValues) => {
    try {
      await saveFirmSettings(parseSubmittedValues(values, firmSettings));

      // After a successfully submit, clear all dirty fields
      formBag.reset({
        ...values,
        hasAcceptedBeFi20Terms: false, // We want to set this to false after a successful submit to avoid several emails being sent on subsequent saves
        hasAcceptedAiPdfReaderTerms: false,
      });

      // Request app variables in order to know if advisor has access to asset bucketing layout
      dispatch(getApplicationVariables());

      toast.success('Firm settings were successfully saved');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Box as="form" mt="24" onSubmit={formBag.handleSubmit(onSubmit)}>
        {saveFirmSettingsError && (
          <Alert message={saveFirmSettingsError.message} variant="error" mb="16" />
        )}

        <FirmLogo
          isFirm
          formBag={formBag}
          fieldName="firmLogoFilename"
          customFirmUrl={firmSettings.customFirmUrl}
          mb="56"
        />

        {firmSettings.showAgreements && (
          <AutoAccountsAgreements
            isFirm
            agreements={firmSettings.agreements}
            formBag={formBag}
            mb="56"
          />
        )}

        <CustomDisclosure isFirm fieldName="customDisclosures" formBag={formBag} mb="56" />

        <TermsAndConditions isFirm fieldName="customTermsAndConditions" formBag={formBag} mb="56" />

        <Assumptions
          isFirm
          firmAssumptions={firmSettings.firmAssumptions}
          formBag={formBag}
          is3dRiskToleranceWorkflowEnabled={firmSettings.is3dRiskToleranceWorkflowEnabled}
          mb="56"
          onOpenCurrentYearAssumptionsModal={() => setCurrentYearAssumptionSettingVisibility(true)}
          onOpenExcessCashModal={() => setIsManageExcessCashModalOpen(true)}
        />

        <SupportEmails formBag={formBag} mb="56" />

        <LandingPage formBag={formBag} options={firmSettings.clientLandingPageOptions} mb="56" />

        <SharingSettings formBag={formBag} mb="56" />

        {firmSettings.displayAssetBucketing && <AssetBucketing formBag={formBag} mb="56" />}

        <ManualAccounts isFirm fieldName="allowAddManualAccount" formBag={formBag} mb="56" />

        <BeFi20 formBag={formBag} mb="56" />

        <PulseCheck formBag={formBag} mb="56" />

        {firmSettings.hasDocumentVault && <DocumentVault formBag={formBag} mb="56" />}

        {firmSettings.pdfImportEnabled && <AIPdfReader formBag={formBag} mb="56" />}

        <MobileAppNotifications
          formBag={formBag}
          mb="56"
          allowDocumentVaultPushNotifications={firmSettings.hasDocumentVault}
          allowNewsfeedPushNotifications={firmSettings.hasNewsfeed}
        />

        <PersonalFinances formBag={formBag} mb="56" />

        <Box display="flex" justifyContent="flex-end" mb="32">
          <Button type="submit" size="large" isLoading={isSavingFirmSettings}>
            Save
          </Button>
        </Box>
      </Box>

      {showCurrentYearAssumptionSetting && (
        <CurrentYearAssumptionSetting
          onClose={() => {
            setCurrentYearAssumptionSettingVisibility(false);
          }}
        />
      )}
      {isManageExcessCashModalOpen && (
        <ManageExcessCashModal
          onClose={() => {
            setIsManageExcessCashModalOpen(false);
          }}
        />
      )}
    </>
  );
};
